/**
 * Encrypted attachments of a secret (E2E media pattern, Susurro style).
 *
 * Each attachment uses a **per-attachment content key** (AES-256-GCM, 32 random
 * bytes) that encrypts file BYTES. That content key is
 * WRAPPED with the vault key (never travels plain). Sensitive
 * (file name, MIME type) is also encrypted in an envelope using the
 * vault key. What server guards is 100% opaque:
 *
 *   fileBytes ─AES-GCM(contentKey, AAD=attachment)──▶ blob            (→ server, opaco)
 *   contentKey ─AES-GCM(vaultKey,  AAD=attachment-key)▶ wrappedKey    (→ server, opaco)
 *   {name,mime} ─AES-GCM(vaultKey, AAD=attachment-meta)▶ metaEnvelope (→ server, opaco)
 *
 * AAD binds each cryptogram to `vaultId:attachmentId`: server cannot
 * move blob/key/metadata to another attachment or vault without open failing.
 * Download+decryption occur ONLY on client (server never sees plaintext).
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "./encoding";
import { openBase64, randomSymmetricKey, sealBase64 } from "./primitives";
import type { AttachmentMeta, ProtectedAttachment } from "./types";

function contentAad(vaultId: string, attachmentId: string): string {
  return `boveda:attachment:v1:${vaultId}:${attachmentId}`;
}
function keyAad(vaultId: string, attachmentId: string): string {
  return `boveda:attachment-key:v1:${vaultId}:${attachmentId}`;
}
function metaAad(vaultId: string, attachmentId: string): string {
  return `boveda:attachment-meta:v1:${vaultId}:${attachmentId}`;
}

export interface EncryptAttachmentInput {
  readonly vaultKey: Uint8Array;
  readonly vaultId: string;
  readonly itemId: string;
  readonly attachmentId: string;
  readonly bytes: Uint8Array;
  readonly meta: AttachmentMeta;
}

/**
 * Encrypts a file into a {@link ProtectedAttachment} (all opaque to server).
 * `size` is plaintext size in bytes: minimal non-sensitive metadata (like
 * associating `vault_id`), useful for UI without decrypting.
 */
export async function encryptAttachment(
  input: EncryptAttachmentInput,
): Promise<ProtectedAttachment> {
  const { vaultKey, vaultId, itemId, attachmentId, bytes, meta } = input;
  const contentKey = randomSymmetricKey();
  try {
    const blob = await sealBase64(contentKey, bytes, contentAad(vaultId, attachmentId));
    const wrappedKey = await sealBase64(vaultKey, contentKey, keyAad(vaultId, attachmentId));
    const metaBytes = utf8Encode(JSON.stringify(meta));
    const metaEnvelope = await sealBase64(vaultKey, metaBytes, metaAad(vaultId, attachmentId));
    return {
      id: attachmentId,
      vaultId,
      itemId,
      wrappedKey,
      metaEnvelope,
      blob,
      size: bytes.length,
    };
  } finally {
    // Zero out content key after use (must not linger in memory).
    contentKey.fill(0);
  }
}

export interface DecryptedAttachment {
  readonly meta: AttachmentMeta;
  readonly bytes: Uint8Array;
}

/** Decrypts an attachment: unwraps content key and opens blob + metadata. */
export async function decryptAttachment(
  vaultKey: Uint8Array,
  attachment: ProtectedAttachment,
): Promise<DecryptedAttachment> {
  const { vaultId, id } = attachment;
  const contentKey = await openBase64(vaultKey, attachment.wrappedKey, keyAad(vaultId, id));
  try {
    const bytes = await openBase64(contentKey, attachment.blob, contentAad(vaultId, id));
    const metaBytes = await openBase64(vaultKey, attachment.metaEnvelope, metaAad(vaultId, id));
    // Trust boundary: envelope sealed by THIS client (AEAD authenticated JSON).
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    const meta = JSON.parse(utf8Decode(metaBytes)) as AttachmentMeta;
    return { meta, bytes };
  } finally {
    contentKey.fill(0);
  }
}

/** Metadata only (name/MIME) without fetching/decrypting full blob. */
export async function decryptAttachmentMeta(
  vaultKey: Uint8Array,
  attachment: Pick<ProtectedAttachment, "id" | "vaultId" | "metaEnvelope">,
): Promise<AttachmentMeta> {
  const metaBytes = await openBase64(
    vaultKey,
    attachment.metaEnvelope,
    metaAad(attachment.vaultId, attachment.id),
  );
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return JSON.parse(utf8Decode(metaBytes)) as AttachmentMeta;
}

export { base64ToBytes, bytesToBase64 };
